import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Table,
  Badge,
  Form,
  InputGroup,
  Card,
  Pagination,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { Member } from "../../lib/mockMember";
import { Kpi } from "./Kpi";
import { ChartCard } from "./ChartCard";
import { fetchPeriodicSalesApi } from "../../api/fetchPeriodicSales";
import { useQuery } from "@tanstack/react-query";
import HomePage from "./HomePage";
import { fmtMoney, pct } from "../../methods/commonMethods";
import { PlaceholderReport } from "./PlaceHolderReport";

const PALETTE = [
  "#4f8cff",
  "#7c5cff",
  "#21c997",
  "#ffb020",
  "#ff6b6b",
  "#26c6da",
  "#ab47bc",
];

const getMonthRange = (str) => {
  const [monthAbbr, yearStr] = str.split(" ");
  const year = 2000 + parseInt(yearStr, 10);
  const month = new Date(`${monthAbbr} 1, 2000`).getMonth();

  const today = new Date();
  const end = new Date(year, month, today.getDate());
  const start = new Date(year, month - 1, today.getDate());

  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return { startDate: fmt(start), endDate: fmt(end) };
};

async function monthlyTrend(members) {
  // const map = new Map<string, { month: string; joins: number; active: number; revenue: number }>();
  const map = new Map();
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i);
    const k = d.toLocaleString("en", { month: "short", year: "2-digit" });
    map.set(k, { month: k, joins: 0, active: 0, revenue: 0 });
  }
  members.forEach((m) => {
    const jd = new Date(m.joinDate);
    const k = jd.toLocaleString("en", { month: "short", year: "2-digit" });
    //if (map.has(k)) map.get(k)!.joins++;

    const t = map.get(k);
    if (t) t.joins++;
  });
  // console.log(map);
  // console.log(Array.from(map.values()));

  // simulate active count and revenue across months
  //
  // ***
  // const totalActive = members.filter((m) => m.status === "Active").length;
  // const totalRev = members.reduce((s, m) => s + m.totalSpend, 0);
  // const keys = Array.from(map.keys());

  // await Promise.all(
  //   keys.map(async (k, idx) => {
  //     // console.log(k); // month, year
  //     const factor = 0.6 + (idx / (keys.length - 1)) * 0.45;
  //     const h = map.get(k);
  //     if (!h) return;

  //     // there's really no way to know how many were active last month, or two months ago
  //     // h.active = Math.round(totalActive * factor);
  //     try {
  //       const g = getMonthRange(k);

  //       let r = await fetchPeriodicSalesApi(g.startDate, g.endDate);

  //       // console.log(r);

  //       h.revenue = r["samount"] || 0;
  //     } catch (error) {
  //       h.revenue = 0;
  //       console.log(error);
  //     }
  //   }),
  // );
  // ****

  // keys.forEach(async (k, idx) => {
  //   const factor = 0.6 + (idx / (keys.length - 1)) * 0.45;
  //   // map.get(k)!.active = Math.round(totalActive * factor);
  //   // map.get(k)!.revenue = Math.round((totalRev / 12) * (0.7 + (idx / keys.length) * 0.6));
  //   const h = map.get(k);
  //   if (h) {
  //     // //for est
  //     h.active = Math.round(totalActive * factor);
  //     h.revenue = Math.round(
  //       (totalRev / 12) * (0.7 + (idx / keys.length) * 0.6),
  //     );
  //   }
  // });
  //
  // console.log(Array.from(map.values()));
  return Array.from(map.values());
}

const getPast5Years = () => {
  const now = new Date();

  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return Array.from({ length: 5 }, (_, i) => {
    const end = new Date(now);
    end.setFullYear(end.getFullYear() - i);

    const start = new Date(end);
    start.setFullYear(start.getFullYear() - 1);

    return { startDate: fmt(start), endDate: fmt(end) };
  });
};

const getPast12Quarters = () => {
  const now = new Date();

  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return Array.from({ length: 12 }, (_, i) => {
    const end = new Date(now);
    end.setMonth(end.getMonth() - i * 3);

    const start = new Date(end);
    start.setMonth(start.getMonth() - 3);

    return { startDate: fmt(start), endDate: fmt(end) };
  });
};

const getPast12Months = () => {
  const now = new Date();
  let t = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i);
    const k = d.toLocaleString("en", { month: "short", year: "2-digit" });
    const g = getMonthRange(k);
    t.push({ startDate: g.startDate, endDate: g.endDate });
  }

  return t;
};

async function getRevenueTrend(range) {
  const map = new Map();
  const now = new Date();

  let dateList = [];

  if (range === "12m") {
    dateList = getPast12Months();
  } else if (range === "12 Quarters") {
    dateList = getPast12Quarters();
    dateList.reverse();
  } else if (range === "5 Years") {
    dateList = getPast5Years();
    dateList.reverse();
  }

  for (const g of dateList) {
    const d = new Date(g.endDate);
    const k = d.toLocaleString("en", { month: "short", year: "2-digit" });
    map.set(k, { range: g, month: k, joins: 0, active: 0, revenue: 0 });
  }

  // console.log(map);

  const keys = Array.from(map.keys());

  await Promise.all(
    keys.map(async (k, idx) => {
      // console.log(k); // month, year
      const factor = 0.6 + (idx / (keys.length - 1)) * 0.45;
      const h = map.get(k);
      if (!h) return;

      // there's really no way to know how many were active last month, or two months ago
      // h.active = Math.round(totalActive * factor);
      try {
        // console.log(h.range.startDate);
        // console.log(h.range.endDate);

        let r = await fetchPeriodicSalesApi(h.range.startDate, h.range.endDate);

        // console.log(r);

        h.revenue = r["samount"] || 0;
      } catch (error) {
        h.revenue = 0;
        // console.log(error);
      }
    }),
  );

  return Array.from(map.values());
}

// { report, members }: { report: ReportKey; members: Member[] }

/**
 * @typedef {Object} MembershipReportProps
 * @property {import("../../components/dashboard/Sidebar").ReportKey} report
 * @property {import("../../lib/mockMember").Member[]} members
 * @property {string} range
 */

/**
 * @param {MembershipReportProps} props
 */
export function MembershipReport({ report, members, range }) {
  // console.log(members);
  // console.log(members.reduce((s, m) => s + m.totalSpend, 0));
  //const trend = useMemo(() => monthlyTrend(members), [members]);

  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const displayMembers = useMemo(() => {
    setCurrentPage(1);
    return members.filter(
      (item) =>
        (item.member_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.id || "").toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, members]);

  const {
    data: gTrend = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["monthlyTrend", members.map((m) => m.id)],
    queryFn: () => monthlyTrend(members),
    staleTime: 1000 * 60 * 15,
  });

  const {
    data: revTrend = [],
    isLoading: isLoadingRev,
    isError: isErrorRev,
  } = useQuery({
    queryKey: ["revTrend", range],
    queryFn: () => getRevenueTrend(range),
    staleTime: 1000 * 60 * 15,
  });

  const total = members.length;

  const active = members.filter((m) => !m.suspended).length;

  const subsElligibleMembers = members.filter((m) => m.subsElligible);
  const numOfSubsElMembers = subsElligibleMembers.length;
  const paid = subsElligibleMembers.filter((m) => m.subscriptionPaid).length;

  let revenue = 0;
  let avgSpend = 0;

  // if (range !== "12m") {
  revenue = revTrend.reduce((s, m) => s + m.revenue, 0);
  avgSpend = total ? Math.round(revenue / total) : 0;
  // } else {
  //   revenue = members.reduce((s, m) => s + m.totalSpend, 0);
  //   avgSpend = total ? Math.round(revenue / total) : 0;
  // }

  // **

  // **

  // pagination

  const perPage = 50;

  const totalPages = Math.ceil(displayMembers.length / perPage);
  const paginatedMembers = displayMembers.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  //

  if (report === "overview") {
    const byType = Object.entries(
      members.reduce((acc, m) => {
        acc[m.member_main_category] = (acc[m.member_main_category] || 0) + 1;
        return acc;
      }, {}),
    ).map(([name, value]) => ({ name, value }));

    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={6} xl={3}>
            <Kpi
              label="Total Members"
              value={total.toLocaleString()}
              delta="+4.2% MoM"
              icon="bi-people-fill"
              tone="primary"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Active Members"
              value={`${active.toLocaleString()} (${pct(active, total)})`}
              delta="+1.8%"
              icon="bi-person-check-fill"
              tone="success"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label="Subs Paid"
              value={`${paid.toLocaleString()} (${pct(paid, numOfSubsElMembers)})`}
              delta="-0.6%"
              icon="bi-credit-card-2-front-fill"
              tone="warning"
            />
          </Col>
          <Col md={6} xl={3}>
            <Kpi
              label={`Total Revenue ${range === "12m" ? "(12m)" : range === "12 Quarters" ? "(12Q)" : "(5y)"}`}
              value={fmtMoney(revenue)}
              delta="+7.4%"
              icon="bi-cash-stack"
              tone="info"
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <ChartCard title="Membership Growth" subtitle="Monthly new joiners">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={gTrend}>
                  <defs>
                    <linearGradient id="gActive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4f8cff" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#4f8cff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gJoin" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#21c997" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#21c997" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  {/* <Area
                    type="monotone"
                    dataKey="active"
                    stroke="#4f8cff"
                    fill="url(#gActive)"
                    name="Active"
                  /> */}
                  <Area
                    type="monotone"
                    dataKey="joins"
                    stroke="#21c997"
                    fill="url(#gJoin)"
                    name="New Joiners"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={4}>
            <ChartCard
              title="By Membership Type"
              subtitle="Distribution across plans"
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byType}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                  >
                    {byType.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={6}>
            <ChartCard
              title="Revenue Trend"
              subtitle={`Est Membership Revenue  ${
                range === "12m"
                  ? "(12M)"
                  : range === "12 Quarters"
                    ? "(12Q)"
                    : "(5y)"
              }`}
            >
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="revenue" fill="#7c5cff" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={6}>
            <ChartCard
              title="Top Spenders"
              subtitle={
                range === "12m"
                  ? "Last 12 months"
                  : range === "12 Quarters"
                    ? "Last 12 Quarters "
                    : "Last 5 Years"
              }
            >
              <Table hover responsive size="sm" className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th className="text-end">Spend (Ksh)</th>
                  </tr>
                </thead>
                <tbody>
                  {[...members]
                    .sort((a, b) => b.totalSpend - a.totalSpend)
                    .slice(0, 8)
                    .map((m) => (
                      <tr key={m.id}>
                        <td>{m.member_name}</td>
                        <td>{m.member_main_category}</td>
                        <td>
                          <Badge
                            bg={m.status === "Active" ? "success" : "secondary"}
                          >
                            {m.status}
                          </Badge>
                        </td>
                        <td className="text-end fw-semibold">
                          {fmtMoney(m.totalSpend, false)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "membership-types") {
    const types = Array.from(
      new Set(members.map((m) => m.member_main_category)),
    );
    const data = types.map((t) => {
      const subset = members.filter((m) => m.member_main_category === t);
      return {
        name: t,
        total: subset.length,
        active: subset.filter((m) => m.status === "Active").length,
        revenue: subset.reduce((s, m) => s + m.totalSpend, 0),
      };
    });
    return (
      <>
        <Row className="g-3 mb-3">
          {/* data.slice(0, 4) */}
          {data.map((d, i) => (
            <Col md={6} xl={3} key={d.name}>
              <Kpi
                label={d.name}
                value={d.total.toString()}
                delta={`${pct(d.active, d.total)} active`}
                icon="bi-collection-fill"
                tone={["primary", "success", "warning", "info"][i % 4]}
              />
            </Col>
          ))}
        </Row>
        <Row className="g-3">
          <Col xl={7}>
            <ChartCard title="Members by Type" subtitle="Total vs active">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="name" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                  <Bar dataKey="total" fill="#4f8cff" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="active" fill="#21c997" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard
              title="Revenue by Type"
              subtitle="Contribution to total revenue"
            >
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="revenue"
                    nameKey="name"
                    outerRadius={110}
                  >
                    {data.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => fmtMoney(Number(v) || 0)}
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "demographics") {
    const genders = ["MALE", "FEMALE", "OTHER"].map((g) => ({
      name: g,
      value: members.filter((m) => m.gender === g).length,
    }));
    const ages = ["18-29", "30-44", "45-59", "60+", "Unknown"].map((a) => ({
      name: a,
      value: members.filter((m) => m.ageBracket === a).length,
    }));
    const male = genders[0].value,
      female = genders[1].value;
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Male"
              value={`${male} (${pct(male, total)})`}
              icon="bi-gender-male"
              tone="info"
              delta=""
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Female"
              value={`${female} (${pct(female, total)})`}
              icon="bi-gender-female"
              tone="danger"
              delta=""
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Gender Ratio (M:F)"
              value={`${(male / Math.max(female, 1)).toFixed(2)} : 1`}
              icon="bi-people"
              tone="primary"
              delta=""
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={5}>
            <ChartCard title="Gender Split">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={genders}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                  >
                    {genders.map((_, i) => (
                      <Cell
                        key={i}
                        fill={["#4f8cff", "#ff6b9d", "#ab47bc"][i]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={7}>
            <ChartCard title="Age Distribution">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ages}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="name" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Bar dataKey="value" fill="#7c5cff" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "retention") {
    const inactive = total - active;
    const byTypeStatus = Array.from(
      new Set(members.map((m) => m.member_main_category)),
    ).map((t) => {
      const sub = members.filter((m) => m.member_main_category === t);
      return {
        name: t,
        Active: sub.filter((m) => !m.suspended).length,
        Inactive: sub.filter((m) => m.suspended).length,
      };
    });
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Active"
              value={active.toString()}
              delta={pct(active, total)}
              icon="bi-person-check-fill"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Inactive"
              value={inactive.toString()}
              delta={pct(inactive, total)}
              icon="bi-person-dash-fill"
              tone="danger"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Retention Rate"
              value={pct(active, total)}
              icon="bi-shield-check"
              tone="primary"
              delta=""
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={10}>
            <ChartCard title="Active vs Inactive by Type">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={byTypeStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="name" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                  <Bar dataKey="Active" stackId="a" fill="#21c997" />
                  <Bar dataKey="Inactive" stackId="a" fill="#ff6b6b" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          {/* <Col xl={5}>
            <ChartCard title="Active Membership Over Time">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={gTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="active"
                    stroke="#4f8cff"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col> */}
        </Row>
      </>
    );
  }

  if (report === "subscriptions") {
    const unpaid = numOfSubsElMembers - paid;
    const paidByType = Array.from(
      new Set(subsElligibleMembers.map((m) => m.member_main_category)),
    ).map((t) => {
      const sub = subsElligibleMembers.filter(
        (m) => m.member_main_category === t,
      );
      return {
        name: t,
        Paid: sub.filter((m) => m.subscriptionPaid).length,
        Unpaid: sub.filter((m) => !m.subscriptionPaid).length,
      };
    });
    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Kpi
              label="Subs Elligible"
              value={numOfSubsElMembers.toString()}
              delta={pct(numOfSubsElMembers, total)}
              icon="bi-check-circle-fill"
              tone="success"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Paid Subscriptions"
              value={paid.toString()}
              delta={pct(paid, numOfSubsElMembers)}
              icon="bi-check-circle-fill"
              tone="success"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Outstanding"
              value={unpaid.toString()}
              delta={pct(unpaid, numOfSubsElMembers)}
              icon="bi-exclamation-triangle-fill"
              tone="warning"
            />
          </Col>
          <Col md={3}>
            <Kpi
              label="Collection Rate"
              value={pct(paid, numOfSubsElMembers)}
              icon="bi-graph-up-arrow"
              tone="primary"
              delta=""
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={7}>
            <ChartCard title="Paid vs Unpaid by Type">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={paidByType}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="name" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  />
                  <Bar dataKey="Paid" fill="#21c997" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Unpaid" fill="#ffb020" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Unpaid Members" subtitle="Need follow-up">
              <Table responsive size="sm" hover className="pbi-table mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Subs Balance (Ksh)</th>
                    {/* <th>Last Payment</th> */}
                  </tr>
                </thead>
                <tbody>
                  {subsElligibleMembers
                    .filter((m) => !m.subscriptionPaid)
                    .sort((a, b) => b.subsAcBal - a.subsAcBal)
                    .slice(0, 10)
                    .map((m) => (
                      <tr key={m.id}>
                        <td>{m.id}</td>
                        <td>{m.member_name}</td>
                        <td>{m.member_main_category}</td>
                        <td>{fmtMoney(m.subsAcBal, false)}</td>
                        {/* <td>
                          {m.lastPaymentDate ? (
                            new Date(m.lastPaymentDate).toLocaleDateString()
                          ) : (
                            <span className="text-muted">Never</span>
                          )}
                        </td> */}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  if (report === "revenue") {
    // this byteTpe is for the 12 months, what about quarterly, past 5 years e.t.c
    // fetch list of customerSales using dateList
    // console.log(members.reduce((s, m) => s + m.totalSpend, 0));
    const byType = Array.from(
      new Set(members.map((m) => m.member_main_category)),
    ).map((t) => {
      const sub = members.filter((m) => m.member_main_category === t);
      const sum = sub.reduce((s, m) => s + m.totalSpend, 0);

      return {
        name: t,
        total: sum,
        avg: Math.round(sum / Math.max(sub.length, 1)),
      };
    });

    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Kpi
              label="Total Revenue"
              value={fmtMoney(revenue)}
              delta="+7.4%"
              icon="bi-cash-stack"
              tone="success"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="Avg Spend / Member"
              value={fmtMoney(avgSpend)}
              delta="+2.1%"
              icon="bi-graph-up"
              tone="primary"
            />
          </Col>
          <Col md={4}>
            <Kpi
              label="ARPU (Active)"
              value={fmtMoney(
                Math.round(
                  members
                    .filter((m) => m.status === "Active")
                    .reduce((s, m) => s + m.totalSpend, 0) /
                    Math.max(active, 1),
                ),
              )}
              icon="bi-coin"
              tone="info"
              delta=""
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col xl={7}>
            <ChartCard
              title={
                range === "12m"
                  ? "Monthly Revenue"
                  : range === "12 Quarters"
                    ? "Quarterly Revenue"
                    : "Yearly Revenue"
              }
            >
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={revTrend}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c5cff" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#7c5cff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="month" stroke="#7a8395" fontSize={12} />
                  <YAxis stroke="#7a8395" fontSize={12} />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#7c5cff"
                    fill="url(#gRev)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
          <Col xl={5}>
            <ChartCard title="Average Spend by Type">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={byType} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis type="number" stroke="#7a8395" fontSize={12} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#7a8395"
                    fontSize={12}
                    width={80}
                  />
                  <Tooltip formatter={(v) => fmtMoney(Number(v) || 0)} />
                  <Bar dataKey="avg" fill="#4f8cff" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  // members directory
  return (
    <MembersDirectory
      key={members.length}
      // members={displayMembers}
      members={paginatedMembers}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      totalPages={totalPages}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
    />
  );
}

/** @param {{members: Member[],searchTerm:string,setSearchTerm: (c: string) => void, totalPages: Number ,currentPage:Number, setCurrentPage: (c: Number) => void}} args */
function MembersDirectory({
  members,
  searchTerm,
  setSearchTerm,
  totalPages,
  currentPage,
  setCurrentPage,
}) {
  // console.log(members);
  // return (
  //   <ChartCard
  //     title="Members Directory"
  //     key={searchTerm}
  //     subtitle={`${members.length} members in current selection`}
  //     action={

  //     }
  //   >

  //   </ChartCard>
  // );
  return (
    <Card className="pbi-chart-card h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="pbi-chart-title">Members Directory</div>
            <div className="pbi-chart-subtitle">{`${members.length} members in current selection`}</div>
          </div>
          <InputGroup size="sm" style={{ width: 340 }}>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              className="home-search"
              placeholder="Search"
              value={searchTerm}
              onChange={(v) => setSearchTerm(v.target.value)}
            />
          </InputGroup>
        </div>
        <div className="pbi-chart-body">
          <div style={{ maxHeight: 400, overflow: "auto" }}>
            <Table
              hover
              responsive
              striped
              bordered
              size="sm"
              className="pbi-table mb-0"
              // key={`${members.length}-${searchTerm}`}
            >
              <thead className="sticky-top bg-white">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Gender</th>
                  <th>Status</th>
                  <th>Pays Subs</th>
                  <th>Subs</th>
                  <th>Subs Balance (Ksh)</th>
                  {/* <th>Joined</th> */}
                  {/* <th className="text-end">Spend</th> */}
                </tr>
              </thead>
              <tbody>
                {members.map((m, index) => (
                  <tr key={`${index}-${m.id}-${m.member_name}`}>
                    <td className="text-muted">{m.id}</td>
                    <td className="fw-semibold">{m.member_name}</td>
                    <td>{m.member_main_category}</td>
                    <td>{m.gender}</td>
                    <td>
                      <Badge
                        bg={m.status === "Active" ? "success" : "secondary"}
                      >
                        {m.status}
                      </Badge>
                    </td>
                    <td>
                      {m.subsElligible ? (
                        <Badge bg="info">Yes</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">
                          No
                        </Badge>
                      )}
                    </td>
                    <td>
                      {m.subsElligible ? (
                        m.subscriptionPaid ? (
                          <Badge bg="info">Paid</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">
                            Unpaid
                          </Badge>
                        )
                      ) : (
                        <></>
                      )}
                    </td>
                    <td>{fmtMoney(m.subsAcBal, false)}</td>
                    {/* <td>{new Date(m.joinDate).toLocaleDateString()}</td> */}
                    {/* <td className="text-end">
                      ${m.totalSpend.toLocaleString()}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination className="mt-2">
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            />
            <Pagination.Item>
              {currentPage} / {totalPages}
            </Pagination.Item>

            <Pagination.Next
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            />
          </Pagination>
        </div>
      </Card.Body>
    </Card>
  );
}
